import axios from "axios";
import { MaterialReactTable, MRT_PaginationState, MRT_SortingState } from "material-react-table";
import { useEffect, useState } from "react";
import { UrlUpdateType } from "use-query-params";
import ReviewRemovalRequestModal from "../../RemovalRequest/ReviewRemovalRequestModal";
import { CompanyRecord } from "../../types";
import { companyRecordColumns } from "../CompanyRecordColumns";
import CompanyEntityHeader from "./CompanyEntityHeader";

interface CompanyRecordsTabProps {
  selectedEntityId?: number;
  setSelectedEntityId: (newValue: string | null | undefined, updateType?: UrlUpdateType) => void;
  onEdit?: (entityId: number) => void;
}

const CompanyRecordsTab = ({ selectedEntityId, setSelectedEntityId, onEdit }: CompanyRecordsTabProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isModalLoading, setIsModalLoading] = useState<boolean>(false);
  const [companyRecords, setCompanyRecords] = useState<CompanyRecord[]>([]);
  const [selectedCompanyWithRemovalRequest, setSelectedCompanyWithRemovalRequest] = useState<CompanyRecord | null>(
    null
  );
  const [rowCount, setRowCount] = useState<number>(0);
  const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 100 });
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState<string>("");

  const fetchCompanyRecords = async () => {
    setIsLoading(true);
    try {
      const { data } = await axios.post("/api/blacklist/getCompanyRecordsPaginated", {
        pageSize: pagination.pageSize,
        pageNumber: pagination.pageIndex,
        searchTerm: globalFilter,
        sortField: sorting.length > 0 ? sorting[0].id : "",
        sortDescending: sorting.length > 0 ? sorting[0].desc : false
      });
      setCompanyRecords(data.data);
      setRowCount(data.count);
    } catch (error) {
      console.error("Error fetching company records", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCompanyWithRemovalRequest = async (companyId: number) => {
    setIsModalLoading(true);
    try {
      const { data } = await axios.get<CompanyRecord>(`/api/blacklist/GetCompanyRecordById?id=${companyId}`);
      if (data.removalRequest) {
        setSelectedCompanyWithRemovalRequest(data);
      } else {
        setSelectedEntityId(undefined);
      }
    } catch (error) {
      console.error("Error fetching company with removal request", error);
    } finally {
      setIsModalLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanyRecords();
  }, [pagination.pageIndex, pagination.pageSize, sorting, globalFilter]);

  useEffect(() => {
    if (!selectedEntityId) return;
    fetchCompanyWithRemovalRequest(selectedEntityId);
  }, [selectedEntityId]);

  const handleCloseModal = (): void => {
    setSelectedCompanyWithRemovalRequest(null);
    setSelectedEntityId(undefined);
  };

  const handleRefreshUpdatedRequest = async (): Promise<void> => {
    await fetchCompanyRecords();
  };

  return (
    <>
      <MaterialReactTable
        enableStickyHeader
        columns={companyRecordColumns(fetchCompanyRecords, onEdit)}
        data={companyRecords}
        initialState={{ columnPinning: { left: ['edit'] } }}
        manualPagination
        manualFiltering
        manualSorting
        onPaginationChange={setPagination}
        onSortingChange={setSorting}
        onGlobalFilterChange={setGlobalFilter}
        rowCount={rowCount}
        state={{
          isLoading: isLoading,
          pagination,
          sorting,
          globalFilter
        }}
        muiTableBodyRowProps={({ row }) => ({
          sx: { backgroundColor: row.index % 2 === 0 ? "#f4f4f4" : "white" }
        })}
      />

      {selectedCompanyWithRemovalRequest?.removalRequest && (
        <ReviewRemovalRequestModal
          open={Boolean(selectedCompanyWithRemovalRequest)}
          handleClose={handleCloseModal}
          removalRequest={selectedCompanyWithRemovalRequest.removalRequest}
          onActionComplete={handleRefreshUpdatedRequest}
          BlacklistDetailsComponent={<CompanyEntityHeader company={selectedCompanyWithRemovalRequest} />}
        />
      )}
    </>
  );
};

export default CompanyRecordsTab;
